import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import {
  fetchProgressAnalytics,
  updateProgress,
  generateAIAdjustment,
  generateLearningInsights,
  addPendingUpdate,
  confirmUpdate,
  rejectUpdate,
  acceptAdjustment,
  dismissAdjustment,
  completeCelebration,
  updateSingleMasteryState,
  addProgressInsight,
  clearError,
  selectMasteryStates,
  selectProgressInsights,
  selectPendingUpdates,
  selectActiveAdjustments,
  selectPendingCelebrations,
  selectProgressAnalytics,
  selectLearningInsights,
  selectProgressSettings,
  selectProgressLoadingState,
} from '../store/slices/adaptiveProgressSlice';
import type { MasteryState, ProgressInsight } from '@coffeeplexity/shared';
import type { ProgressUpdate } from '../components/ai/RealTimeProgressUpdater';
import type { ProgressAdjustment } from '../components/ai/AdaptiveProgressBar';

export function useAdaptiveProgress() {
  const dispatch = useAppDispatch();

  // Selectors
  const masteryStates = useAppSelector(selectMasteryStates);
  const progressInsights = useAppSelector(selectProgressInsights);
  const pendingUpdates = useAppSelector(selectPendingUpdates);
  const activeAdjustments = useAppSelector(selectActiveAdjustments);
  const pendingCelebrations = useAppSelector(selectPendingCelebrations);
  const analytics = useAppSelector(selectProgressAnalytics);
  const learningInsights = useAppSelector(selectLearningInsights);
  const settings = useAppSelector(selectProgressSettings);
  const loadingState = useAppSelector(selectProgressLoadingState);

  // Actions
  const fetchAnalytics = useCallback(
    (params: { userId: string; timeRange: '7d' | '30d' | '90d' | '1y' }) => {
      dispatch(fetchProgressAnalytics(params));
    },
    [dispatch]
  );

  const recordProgressUpdate = useCallback(
    (update: Omit<ProgressUpdate, 'id' | 'timestamp'>) => {
      // Add optimistic update first
      const optimisticUpdate: ProgressUpdate = {
        ...update,
        id: `optimistic_${Date.now()}`,
        timestamp: new Date().toISOString(),
        optimistic: true,
      };

      dispatch(addPendingUpdate(optimisticUpdate));

      // Then dispatch the actual update
      dispatch(updateProgress(update));
    },
    [dispatch]
  );

  const confirmProgressUpdate = useCallback(
    (updateId: string) => {
      dispatch(confirmUpdate(updateId));
    },
    [dispatch]
  );

  const rejectProgressUpdate = useCallback(
    (updateId: string, reason: string) => {
      dispatch(rejectUpdate({ updateId, reason }));
    },
    [dispatch]
  );

  const requestAIAdjustment = useCallback(
    (params: { userId: string; skillId: string; context: any }) => {
      dispatch(generateAIAdjustment(params));
    },
    [dispatch]
  );

  const acceptProgressAdjustment = useCallback(
    (adjustmentId: string) => {
      dispatch(acceptAdjustment(adjustmentId));
    },
    [dispatch]
  );

  const dismissProgressAdjustment = useCallback(
    (adjustmentId: string) => {
      dispatch(dismissAdjustment(adjustmentId));
    },
    [dispatch]
  );

  const completeMilestoneCelebration = useCallback(
    (celebrationId: string) => {
      dispatch(completeCelebration(celebrationId));
    },
    [dispatch]
  );

  const updateMastery = useCallback(
    (masteryState: MasteryState) => {
      dispatch(updateSingleMasteryState(masteryState));
    },
    [dispatch]
  );

  const addInsight = useCallback(
    (insight: ProgressInsight) => {
      dispatch(addProgressInsight(insight));
    },
    [dispatch]
  );

  const generateInsights = useCallback(
    (params: { userId: string; masteryStates: MasteryState[] }) => {
      dispatch(generateLearningInsights(params));
    },
    [dispatch]
  );

  const clearProgressError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  // Auto-generate insights when mastery states change
  useEffect(() => {
    if (masteryStates.length > 0) {
      const userId = 'current-user'; // This should come from auth state
      generateInsights({ userId, masteryStates });
    }
  }, [masteryStates, generateInsights]);

  // Auto-request AI adjustments for declining skills
  useEffect(() => {
    const decliningSkills = masteryStates.filter(
      state => state.trend === 'declining' && state.confidence > 0.7
    );

    decliningSkills.forEach(skill => {
      const userId = 'current-user'; // This should come from auth state
      requestAIAdjustment({
        userId,
        skillId: skill.skillId,
        context: {
          currentMastery: skill.masteryLevel,
          trend: skill.trend,
          evidenceCount: skill.evidenceCount,
        },
      });
    });
  }, [masteryStates, requestAIAdjustment]);

  return {
    // State
    masteryStates,
    progressInsights,
    pendingUpdates,
    activeAdjustments,
    pendingCelebrations,
    analytics,
    learningInsights,
    settings,
    loadingState,

    // Actions
    fetchAnalytics,
    recordProgressUpdate,
    confirmProgressUpdate,
    rejectProgressUpdate,
    requestAIAdjustment,
    acceptProgressAdjustment,
    dismissProgressAdjustment,
    completeMilestoneCelebration,
    updateMastery,
    addInsight,
    generateInsights,
    clearProgressError,

    // Computed values
    hasActiveUpdates: pendingUpdates.length > 0,
    hasActiveAdjustments: activeAdjustments.length > 0,
    hasPendingCelebrations: pendingCelebrations.length > 0,
    overallProgress: analytics?.overallProgress.currentLevel || 0,
    progressRate: analytics?.overallProgress.progressRate || 0,
    streakDays: analytics?.overallProgress.streakDays || 0,
  };
}

// Specialized hooks for specific use cases
export function useProgressUpdates() {
  const {
    pendingUpdates,
    recordProgressUpdate,
    confirmProgressUpdate,
    rejectProgressUpdate,
    settings,
  } = useAdaptiveProgress();

  return {
    updates: pendingUpdates,
    recordUpdate: recordProgressUpdate,
    confirmUpdate: confirmProgressUpdate,
    rejectUpdate: rejectProgressUpdate,
    showOptimistic: settings.showOptimisticUpdates,
  };
}

export function useProgressAdjustments() {
  const {
    activeAdjustments,
    acceptProgressAdjustment,
    dismissProgressAdjustment,
    requestAIAdjustment,
  } = useAdaptiveProgress();

  return {
    adjustments: activeAdjustments,
    acceptAdjustment: acceptProgressAdjustment,
    dismissAdjustment: dismissProgressAdjustment,
    requestAdjustment: requestAIAdjustment,
  };
}

export function useProgressCelebrations() {
  const { pendingCelebrations, completeMilestoneCelebration, settings } =
    useAdaptiveProgress();

  return {
    celebrations: pendingCelebrations,
    completeCelebration: completeMilestoneCelebration,
    celebrationsEnabled: settings.celebrationEnabled,
  };
}

export function useProgressAnalytics() {
  const { analytics, learningInsights, fetchAnalytics, loadingState } =
    useAdaptiveProgress();

  return {
    analytics,
    insights: learningInsights,
    fetchAnalytics,
    isLoading: loadingState.isLoading,
    error: loadingState.error,
  };
}

export function useMasteryTracking() {
  const { masteryStates, updateMastery, progressInsights, addInsight } =
    useAdaptiveProgress();

  const getMasteryBySkill = useCallback(
    (skillId: string) => masteryStates.find(state => state.skillId === skillId),
    [masteryStates]
  );

  const getSkillsByCategory = useCallback(
    (category: string) =>
      masteryStates.filter(state => state.skillCategory === category),
    [masteryStates]
  );

  const getImprovingSkills = useCallback(
    () => masteryStates.filter(state => state.trend === 'improving'),
    [masteryStates]
  );

  const getDecliningSkills = useCallback(
    () => masteryStates.filter(state => state.trend === 'declining'),
    [masteryStates]
  );

  return {
    masteryStates,
    progressInsights,
    updateMastery,
    addInsight,
    getMasteryBySkill,
    getSkillsByCategory,
    getImprovingSkills,
    getDecliningSkills,
  };
}
